import { useParams } from 'react-router-dom';

import { FullScreenLoading, NotFound404 } from '@backrs_ui/common-ui';

import {
  GRADE_INPUT_PROFILE_FIELDS,
  useGradeChangeConfirm,
} from '@backrs_ui/grow-ui/src/components/GradeChangeConfirm/useGradeChangeConfirm';
import {
  selectIsFeatureFlagEnabled,
  FEATURE_FLAGS,
} from '@backrs_ui/grow-ui/src/redux/featureFlags/featureFlags';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useFetchProfileValueOptionsQuery,
  useFetchUserProfileInfoQuery,
  useUpdateUserProfileMutation,
  useFetchPublicProfileInfoQuery,
  useFetchProfilePermissionsQuery,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';
import { selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';
import { isRefusalOrMissing } from '@backrs_ui/grow-ui/src/utils/fetch/refusal';

import { ViewPermissionDenied } from './atoms/ViewPermissionDenied';
import { ProfilePage } from './ProfilePage';
import { useAgentContext } from './ProfilePageContainer.agentContext';
import { PublicProfile } from './PublicProfile';
import { CreateFieldConfigProps, ProfileValue } from './types/profile.types';
import { handleUpdatingAnswer } from './utils/fields.helpers';

export const ProfilePageContainer = () => {
  const { profile } = useParams();

  const { isLoading: isLoadingAutoComplete } = useFetchProfileValueOptionsQuery(undefined);
  const { data: userProfile, isLoading: isLoadingUserProfile } =
    useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS);
  const [updateProfile] = useUpdateUserProfileMutation();
  const { confirmGradeChange, gradeChangeModal } = useGradeChangeConfirm();

  const loggedInUserId = useAppSelector(selectUserId);
  const isCoachingEnabled = useAppSelector(selectIsFeatureFlagEnabled(FEATURE_FLAGS.COACHING));

  const { data: permissions, isLoading: isLoadingPermissions } = useFetchProfilePermissionsQuery(
    { userId: profile },
    { skip: !profile },
  );

  const {
    data: publicProfile,
    isLoading: isLoadingPublicProfile,
    error: publicProfileError,
  } = useFetchPublicProfileInfoQuery(
    { userId: profile },
    { skip: !profile || isLoadingPermissions || !permissions?.view_profile.allowed },
  );

  const isViewingOwnProfile = !profile || loggedInUserId?.toString() === profile;
  const profileToUse = isViewingOwnProfile ? userProfile : publicProfile;

  useAgentContext(profile ? 'Public Profile' : 'Profile', isViewingOwnProfile);

  const purposeVideoUrl = profileToUse?.profile?.documentsPurposeVideo?.[0]?.uri;

  // Check permissions before loading to avoid infinite spinner when access is denied
  if (profile && !isLoadingPermissions && !permissions?.view_profile.allowed) {
    return <ViewPermissionDenied message={permissions?.view_profile.message} />;
  }

  if (!isViewingOwnProfile && isRefusalOrMissing(publicProfileError)) {
    return <NotFound404 />;
  }

  const showLoading =
    isLoadingAutoComplete ||
    isLoadingUserProfile ||
    isLoadingPublicProfile ||
    isLoadingPermissions ||
    !profileToUse;

  if (showLoading) {
    return <FullScreenLoading />;
  }

  if (profile) {
    return (
      <PublicProfile
        profile={{ ...profileToUse, purposeVideoUrl }}
        userId={loggedInUserId}
        isCoachingEnabled={isCoachingEnabled}
        isOwnProfile={isViewingOwnProfile}
      />
    );
  }

  const updateAnswer = async (
    key: string,
    value: ProfileValue[],
    displayProperty?: string,
    fieldConfig?: CreateFieldConfigProps,
  ) => {
    // Cancelling leaves the field as it was rather than saving and asking after.
    // Clearing a year counts: it can finish a student's program.
    if (GRADE_INPUT_PROFILE_FIELDS.has(key)) {
      const proposed = value?.[0] ?? null;

      if (typeof proposed === 'object' && proposed !== null) {
        // A year is only ever a scalar; anything else isn't ours to preview.
      } else if (!(await confirmGradeChange({ [key]: proposed }))) {
        return;
      }
    }

    handleUpdatingAnswer(updateProfile, key, value, displayProperty, fieldConfig);
  };

  return (
    <>
      {gradeChangeModal}
      <ProfilePage handleUpdatingAnswer={updateAnswer} userProfile={userProfile} />
    </>
  );
};
